import axios from 'axios';

/**
 * Extracts a human-readable error message from any error.
 *
 * Handles:
 * - Axios errors with { message: "..." } response body (our API standard)
 * - Axios errors with { Message: "..." } response body (legacy casing)
 * - Validation errors (RFC 7807 with { errors: { field: [...] } })
 * - Network errors (no response)
 * - Plain Error instances
 * - Unknown error types
 */
export function getErrorMessage(
  error: unknown,
  fallback = 'Something went wrong'
): string {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data;

    if (data) {
      // Standard API error response: { message: "..." }
      if (typeof data.message === 'string' && data.message) return data.message;

      // Legacy casing: { Message: "..." }
      if (typeof data.Message === 'string' && data.Message) return data.Message;

      // RFC 7807 validation errors: { errors: { field: ["msg"] } }
      if (data.errors && typeof data.errors === 'object') {
        const firstField = Object.values(data.errors)[0];
        if (Array.isArray(firstField) && firstField.length > 0) {
          return String(firstField[0]);
        }
      }

      // Plain string response body
      if (typeof data === 'string' && data) return data;
    }

    // No response = network error
    if (!error.response) return 'Connection error. Please check your network.';

    return fallback;
  }

  // ApiError from @billda/api (openapi-fetch client). Duck-typed to avoid a
  // circular dependency; carries the same backend { message } / { Message } /
  // { errors } bodies as an AxiosError response, so messages stay identical.
  if (error instanceof Error && error.name === 'ApiError') {
    const body = (error as { body?: unknown }).body;

    if (body && typeof body === 'object') {
      const data = body as Record<string, unknown>;

      if (typeof data.message === 'string' && data.message) {
        return data.message;
      }
      if (typeof data.Message === 'string' && data.Message) {
        return data.Message;
      }

      if (data.errors && typeof data.errors === 'object') {
        const firstField = Object.values(data.errors)[0];
        if (Array.isArray(firstField) && firstField.length > 0) {
          return String(firstField[0]);
        }
      }
    }

    if (typeof body === 'string' && body) {
      return body;
    }

    return fallback;
  }

  if (error instanceof Error) return error.message || fallback;
  if (typeof error === 'string') return error;
  return fallback;
}
